import { useAuth, useUser } from "@clerk/react";
import { usePostHog } from "@posthog/react";
import { useCallback, useEffect, useMemo } from "react";

import { useConsumerReferral } from "../../hooks/useConsumerReferral";
import { useDashboardShellCapabilities } from "../../hooks/useDashboardShellCapabilities";
import type { ReferralIdentity } from "../../lib/dashboard-settings";
import {
  captureReferralSurfaceClicked,
  captureReferralSurfaceShown,
  type ReferralCapture,
} from "../../lib/referral-analytics";
import { referralSurfaceCopy } from "../../lib/referral-surface-copy";
import { Dialog, DialogContent } from "../ui/dialog";
import { ReferralShareActions } from "./referral-share-actions";

export interface ReferralShareSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Target-area label when the buyer is queued for a pre-launch metro, else null. */
  waitlistArea: string | null;
}

/**
 * The share sheet behind the waitlist card's "Share your invite link" /
 * "Share again" CTA (plan 317 task 1483, dark until now). Same shared
 * `ReferralShareActions` as every other referral surface — one minted link.
 *
 * Unlike the banner/prompt this is USER-INITIATED, so it always renders the
 * link even with nothing to promise (the buyer asked to share); the copy
 * simply omits any reward line in that case rather than inventing one. The
 * body mounts only while open, so the link is not fetched until the tap.
 */
export function ReferralShareSheet({
  open,
  onOpenChange,
  waitlistArea,
}: ReferralShareSheetProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open ? <ReferralShareSheetBody waitlistArea={waitlistArea} /> : null}
    </Dialog>
  );
}

function ReferralShareSheetBody({ waitlistArea }: { waitlistArea: string | null }) {
  const { getToken } = useAuth();
  const { user } = useUser();
  const posthog = usePostHog();
  const referral = useConsumerReferral(getToken);
  const { marketplaceEnabled } = useDashboardShellCapabilities();
  const identity = useMemo<ReferralIdentity>(
    () => ({
      firstName: user?.firstName ?? null,
      lastName: user?.lastName ?? null,
      email: user?.primaryEmailAddress?.emailAddress ?? null,
    }),
    [user?.firstName, user?.lastName, user?.primaryEmailAddress?.emailAddress],
  );
  const capture = useCallback<ReferralCapture>(
    (event, properties) => posthog?.capture?.(event, properties),
    [posthog],
  );
  const copy = referral.isLoading
    ? null
    : referralSurfaceCopy({
        incentive: referral.incentive,
        waitlistArea,
        marketplaceLive: marketplaceEnabled,
      });
  const incentive = copy?.incentive ?? "waitlist";

  // One impression per open, once the promise has resolved.
  const settled = !referral.isLoading;
  useEffect(() => {
    if (settled) captureReferralSurfaceShown(capture, "waitlist_card", incentive);
    // Keyed on `settled` only: the sheet remounts per open, so this is one
    // impression per open and never re-fires on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  return (
    <DialogContent
      title={copy?.headline ?? "Share your invite link"}
      description={
        copy?.body ??
        "Friends who join through your link are attributed to you automatically."
      }
    >
      <ReferralShareActions
        referral={referral}
        identity={identity}
        layout="compact"
        onAction={(action) =>
          captureReferralSurfaceClicked(capture, "waitlist_card", incentive, action)
        }
      />
    </DialogContent>
  );
}
